import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { RunPayrollInput, RunPayrollResponse } from './contractsMirror.js';

/**
 * The money-committing Run Payroll mutation (CARVE-07 / T-22-payroll).
 *
 * Payroll is the most sensitive screen — Run Payroll commits a locked, immutable pay run. This
 * mutation is DELIBERATELY pessimistic:
 *
 *   • NO optimistic UI. There is intentionally NO `onMutate` and NO `setQueryData` BEFORE the server
 *     responds — the committed run is NEVER assumed. The UI shows a pending state (mutation.isPending)
 *     and renders ONLY the server-returned locked run. A wrong/duplicate run must never be shown as
 *     committed before the server confirms it.
 *   • Idempotent server: a second run for the same period returns the EXISTING run (`duplicate:true`),
 *     never a second commit.
 *
 * The request rides `apiFetch` (credentials + CSRF double-submit + tenant header baked in) and sends
 * NO tenantId — the server resolves the tenant from the principal (SEC-11). On success the
 * `['payroll']` cache is invalidated so the Center + History re-sync to the committed run.
 */
export function useRunPayrollMutation() {
  const queryClient = useQueryClient();
  return useMutation<RunPayrollResponse, Error, RunPayrollInput>({
    mutationFn: (input) =>
      apiFetchJson<RunPayrollResponse>('/api/v1/payroll/run', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    // No onMutate — the run is NEVER optimistically committed. Only after the server returns the
    // locked run do we invalidate so the views re-fetch the server truth (D-03 / T-22-payroll).
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll'] });
    },
  });
}
